import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "./supabaseClient";
import { applyRecordUpdatesToIndexes, buildDisplayRecords, loadRecordIndexes } from "./recordsCheck";

export default function SideBySideRecordsTable({ title, excelFile, recordType = "catchAndRelease", scope = "senior" }) {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterText, setFilterText] = useState("");

  useEffect(() => {
    if (!excelFile) { setError("No file specified."); setLoading(false); return; }

    let cancelled = false;
    const loadRecords = async () => {
      setLoading(true);
      setRecords([]);
      setError(null);
      setFilterText("");

      try {
        const baseIndexes = await loadRecordIndexes();
        const { data, error: dbError } = await supabase
          .from("catches")
          .select("species, location, weight_kg, angler_name, weigh_date, is_junior, is_catch_and_release")
          .order("weigh_date", { ascending: true })
          .order("created_at", { ascending: true });

        if (dbError) throw dbError;

        const mergedIndexes = applyRecordUpdatesToIndexes(baseIndexes, data ?? []);
        const parsed = buildDisplayRecords(mergedIndexes, recordType, scope);
        if (!cancelled) setRecords(parsed);
      } catch (err) {
        if (!cancelled) setError(err.message || "Failed to load records.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadRecords();
    return () => { cancelled = true; };
  }, [excelFile, recordType, scope]);

  const filtered = filterText.trim()
    ? records.filter((r) =>
        [r.species, r.boatCaptor, r.shoreCaptor]
          .join(" ")
          .toLowerCase()
          .includes(filterText.toLowerCase())
      )
    : records;

  return (
    <div className="page-shell min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">

        <div className="mb-8 flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="standalone-only group inline-flex items-center gap-1.5 rounded-full border border-cyan-400/20 bg-white/5 px-4 py-2 text-sm font-medium text-slate-200 shadow-lg shadow-black/20 backdrop-blur transition hover:-translate-x-0.5 hover:border-cyan-300/40 hover:bg-cyan-400/10 hover:text-white"
          >
            ← Back
          </button>
          <h1 className="heading-glow text-2xl font-bold text-white sm:text-3xl">{title}</h1>
        </div>

        <div className="mb-6">
          <input
            type="text"
            placeholder="Search species or captor…"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="w-full max-w-sm rounded-xl border border-white/20 bg-slate-900/80 px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-300/40 placeholder:text-slate-500"
          />
        </div>

        {loading ? (
          <p className="text-cyan-200">Loading records…</p>
        ) : error ? (
          <p className="text-red-300">Error: {error}</p>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-white/10">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 bg-slate-800/80">
                  <th colSpan={3} className="border-r border-white/10 px-4 py-3 text-center text-xs font-semibold uppercase tracking-widest text-cyan-300">
                    Boat Record
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-widest text-white/60">
                    Species
                  </th>
                  <th colSpan={3} className="border-l border-white/10 px-4 py-3 text-center text-xs font-semibold uppercase tracking-widest text-emerald-300">
                    Shore Record
                  </th>
                </tr>
                <tr className="border-b border-white/10 bg-slate-900/60 text-xs text-slate-400">
                  <th className="px-3 py-2 text-left font-medium">Year</th>
                  <th className="px-3 py-2 text-left font-medium">Captor</th>
                  <th className="border-r border-white/10 px-3 py-2 text-left font-medium">Weight</th>
                  <th className="px-3 py-2"></th>
                  <th className="border-l border-white/10 px-3 py-2 text-left font-medium">Year</th>
                  <th className="px-3 py-2 text-left font-medium">Captor</th>
                  <th className="px-3 py-2 text-left font-medium">Weight</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length ? filtered.map((r, i) => (
                  <tr
                    key={i}
                    className={`border-b border-white/5 transition hover:bg-white/5 ${i % 2 === 0 ? "bg-slate-950/40" : "bg-slate-900/30"}`}
                  >
                    <td className="px-3 py-2.5 text-slate-400">{r.boatYear}</td>
                    <td className="px-3 py-2.5 text-slate-200">{r.boatCaptor}</td>
                    <td className="border-r border-white/10 px-3 py-2.5 font-medium text-cyan-200">{r.boatWeight}</td>
                    <td className="px-3 py-2.5 text-center font-semibold text-white">{r.species}</td>
                    <td className="border-l border-white/10 px-3 py-2.5 text-slate-400">{r.shoreYear}</td>
                    <td className="px-3 py-2.5 text-slate-200">{r.shoreCaptor}</td>
                    <td className="px-3 py-2.5 font-medium text-emerald-200">{r.shoreWeight}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={7} className="px-4 py-6 text-center text-slate-400">No records match your search.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <p className="mt-4 text-xs text-slate-500">Weights shown as lbs · oz · dr</p>
      </div>
    </div>
  );
}
